import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useRoles, useRolePermissions } from "../../../../../hooks/query";
import { useAuth } from "../../../../../hooks";

export default function useCreateRole() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { selectedOrganization } = useAuth();
    const { useCreate } = useRoles();
    const { useAssign } = useRolePermissions();
    const createRoleSvc = useCreate();
    const assignPermissionsSvc = useAssign();

    const handleCreateRole = async (values: any) => {
        if (!selectedOrganization?.Id) {
            throw new Error("Select an organization before creating a role");
        }

        try {
            const response = await createRoleSvc.mutateAsync({
                Name: values.Name,
                Description: values.Description,
                OrganizationId: selectedOrganization.Id,
            });

            const roleId = response?.id || response?.Id;
            const permissionIds = (values.Permissions || [])
                .map((p: any) => p?.Id)
                .filter(Boolean);

            if (roleId && permissionIds.length) {
                await assignPermissionsSvc.mutateAsync({
                    RoleId: roleId,
                    PermissionIds: permissionIds,
                });
            }

            queryClient.invalidateQueries({ queryKey: ["roles"] });
            navigate("/admin/roles");
        } catch (error) {
            console.error("Error creating role:", error);
            throw error;
        }
    };

    return {
        handleCreateRole,
        createRoleSvc: {
            ...createRoleSvc,
            isPending: createRoleSvc.isPending || assignPermissionsSvc.isPending,
            isError: createRoleSvc.isError || assignPermissionsSvc.isError,
            error: createRoleSvc.error || assignPermissionsSvc.error,
            isSuccess: createRoleSvc.isSuccess && !assignPermissionsSvc.isError,
        },
    };
}
